import React, {useState, useEffect} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Typography,
	Box,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	CircularProgress,
	TablePagination,
	Chip,
} from '@material-ui/core';
import {History} from '@material-ui/icons';
import {useOrders} from '../context/OrdersContext';
import API from '../../../../network/dashboardAPI';
import moment from 'moment';

const useStyles = makeStyles((theme) => ({
	dialog: {
		'& .MuiDialog-paper': {
			minWidth: 800,
			maxHeight: '80vh',
		},
	},
	dialogTitle: {
		borderBottom: `1px solid ${theme.palette.divider}`,
	},
	orderInfo: {
		padding: theme.spacing(2),
		backgroundColor: '#f5f7fa',
		borderRadius: 8,
		marginBottom: theme.spacing(2),
	},
	tableContainer: {
		maxHeight: 500,
	},
	modifiedCell: {
		backgroundColor: '#fff3e0',
		color: '#ff9800',
		fontWeight: 600,
	},
}));

const OrderHistoryDialog = () => {
	const classes = useStyles();
	const {state, actions} = useOrders();
	const {historyDialogOpen, selectedOrder} = state;

	const [loading, setLoading] = useState(false);
	const [historyList, setHistoryList] = useState([]);
	const [totalCount, setTotalCount] = useState(0);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);

	useEffect(() => {
		if (historyDialogOpen && selectedOrder) {
			fetchHistory(0, rowsPerPage);
		}
	}, [historyDialogOpen, selectedOrder]);

	const fetchHistory = async (pageNum = page, limit = rowsPerPage) => {
		if (!selectedOrder?.order_id) return;

		setLoading(true);
		try {
			const body = {
				order_id: selectedOrder.order_id,
				page: pageNum + 1, // API uses 1-based pagination
				limit: limit,
			};

			const response = await API.getUpdateSlotAndDeliveryAt(body)
				.then(res => res.json());

			if (response.success) {
				setHistoryList(response.data.list || []);
				setTotalCount(response.data.total || 0);
			}
		} catch (error) {
			console.error('Failed to fetch order history:', error);
		} finally {
			setLoading(false);
		}
	};

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
		fetchHistory(newPage, rowsPerPage);
	};

	const handleChangeRowsPerPage = (event) => {
		const newRowsPerPage = parseInt(event.target.value, 10);
		setRowsPerPage(newRowsPerPage);
		setPage(0);
		fetchHistory(0, newRowsPerPage);
	};

	const handleClose = () => {
		setPage(0);
		setRowsPerPage(10);
		setHistoryList([]);
		actions.closeHistoryDialog();
	};

	const getModifiedClass = (obj, key) => {
		if (typeof obj !== 'object' || !obj) return '';
		return Object.keys(obj).find(objKey => objKey === key) ? classes.modifiedCell : '';
	};

	const getStatusLabel = (status) => {
		const statusMap = {
			'0': 'Order Saved',
			'1': 'Order Removed',
			'2': 'Order Placed',
			'3': 'Order Accepted',
			'4': 'Order Packed',
			'5': 'Order Dispatched',
			'6': 'Order Delivered',
			'7': 'Order Cancelled',
			'8': 'Order Arrived',
			'-1': 'Waiting Payment',
		};
		return statusMap[String(status)] || status;
	};

	const formatHistoryValue = (field, value) => {
		if (!value && value !== 0) return '-';

		// Format dates for timeslot and delivered_at
		if (field === 'timeslot_start_time' || field === 'delivered_at') {
			return moment(value).format('DD/MMM/YY - hh:mm A');
		}

		// Format order status
		if (field === 'order_status') {
			return getStatusLabel(value);
		}

		// For other fields, return as-is
		return value;
	};

	if (!selectedOrder) return null;

	return (
		<Dialog
			open={historyDialogOpen}
			onClose={handleClose}
			maxWidth="lg"
			fullWidth
			className={classes.dialog}
		>
			<DialogTitle className={classes.dialogTitle}>
				<Box display="flex" alignItems="center" gap={1}>
					<History color="primary"/>
					<Typography variant="h6">Order History</Typography>
				</Box>
			</DialogTitle>

			<DialogContent style={{paddingTop: 24}}>
				{/* Order Info */}
				<Box className={classes.orderInfo}>
					<Typography variant="subtitle2" color="textSecondary">
						Order Details
					</Typography>
					<Typography variant="h6">
						Order #{selectedOrder.order_id}
					</Typography>
					<Typography variant="body2" color="textSecondary">
						Customer: {selectedOrder.customer_detail?.name || 'N/A'}
					</Typography>
				</Box>

				{/* History Table */}
				{loading ? (
					<Box display="flex" justifyContent="center" padding={4}>
						<CircularProgress/>
					</Box>
				) : historyList.length === 0 ? (
					<Typography variant="body2" color="textSecondary" align="center" style={{padding: 32}}>
						No update history found
					</Typography>
				) : (
					<>
						<TableContainer className={classes.tableContainer}>
							<Table size="small" stickyHeader>
								<TableHead>
									<TableRow>
										<TableCell>Order ID</TableCell>
										<TableCell>Field</TableCell>
										<TableCell>Value New</TableCell>
										<TableCell>Value Old</TableCell>
										<TableCell>Comment</TableCell>
										<TableCell>User Name</TableCell>
										<TableCell>Created At</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{historyList.map((row, idx) => (
										<TableRow key={idx} hover>
											<TableCell className={getModifiedClass(row.modified, 'order_id')}>
												{row.order_id}
											</TableCell>
											<TableCell className={getModifiedClass(row.modified, 'field')}>
												<Chip
													label={row.field || 'N/A'}
													size="small"
													variant="outlined"
												/>
											</TableCell>
											<TableCell className={getModifiedClass(row.modified, 'value_new')}>
												<strong>
													{formatHistoryValue(row.field, row.value_new)}
												</strong>
											</TableCell>
											<TableCell className={getModifiedClass(row.modified, 'value_old')}>
												{formatHistoryValue(row.field, row.value_old)}
											</TableCell>
											<TableCell className={getModifiedClass(row.modified, 'comment')}>
												{row.comment || '-'}
											</TableCell>
											<TableCell className={getModifiedClass(row.modified, 'user_name')}>
												{row.user_name || 'System'}
											</TableCell>
											<TableCell className={getModifiedClass(row.modified, 'createdAt')}>
												{row.createdAt
													? moment(row.createdAt).format('DD/MMM/YY - hh:mm A')
													: '-'}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>

						<TablePagination
							component="div"
							count={totalCount}
							page={page}
							onPageChange={handleChangePage}
							rowsPerPage={rowsPerPage}
							onRowsPerPageChange={handleChangeRowsPerPage}
							rowsPerPageOptions={[5, 10, 25, 50]}
						/>
					</>
				)}
			</DialogContent>

			<DialogActions>
				<Button onClick={handleClose}>Close</Button>
			</DialogActions>
		</Dialog>
	);
};

export default OrderHistoryDialog;
